import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './ProductQualitySupport.css';

const ProductQualitySupport = ({ projectCode, onBack }) => {
  const [qualityData, setQualityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchQualityData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectCode]);

  const fetchQualityData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/summary-invoice/${projectCode}/quality-support`);
      setQualityData(response.data.results || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching quality data:', err);
      setError('Failed to load product quality support data.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'R': 'Rework',
      'E': 'Excess',
      'S': 'Scrap',
      'C': 'Complete'
    };
    return statusMap[status] || status;
  };

  if (loading) {
    return (
      <div className="quality-support-container" role="main" aria-busy="true">
        <div className="loading-spinner" aria-live="polite">
          <span className="sr-only">Loading product quality support data...</span>
          Loading...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quality-support-container" role="main">
        <div className="error-message" role="alert">
          {error}
        </div>
        <button onClick={onBack} className="btn-secondary">
          Back to Invoice
        </button>
      </div>
    );
  }

  return (
    <div className="quality-support-container" role="main">
      <header className="page-header">
        <h1 id="page-title">Product Quality Support</h1>
        <p className="page-description">
          Project Code: <strong>{projectCode}</strong>
        </p>
      </header>

      <div className="action-bar no-print">
        <button onClick={onBack} className="btn-secondary" aria-label="Back to invoice">
          Back to Invoice
        </button>
        <button onClick={() => window.print()} className="btn-secondary" aria-label="Print report">
          Print
        </button>
      </div>

      {qualityData.length === 0 ? (
        <div className="no-data-message" role="status">
          No product quality support data available for this project.
        </div>
      ) : (
        <div className="quality-table-wrapper">
          <table 
            className="quality-table" 
            role="table"
            aria-labelledby="page-title"
          >
            <caption className="sr-only">
              Product quality support details for work orders
            </caption>
            <thead>
              <tr>
                <th scope="col">Work Order</th>
                <th scope="col">Model</th>
                <th scope="col">PIC Name</th>
                <th scope="col">WO Quantity</th>
                <th scope="col">Status</th>
                <th scope="col">Quantity</th>
                <th scope="col">NPF Rate</th>
                <th scope="col">Unit Rate</th>
                <th scope="col">Total Amount</th>
              </tr>
            </thead>
            <tbody>
              {qualityData.map((wo, woIndex) => (
                <React.Fragment key={wo.woNumber || woIndex}>
                  {wo.statusDetails && wo.statusDetails.length > 0 ? (
                    wo.statusDetails.map((status, statusIndex) => (
                      <tr 
                        key={`${wo.woNumber}-${statusIndex}`}
                        className={woIndex % 2 === 0 ? 'row-even' : 'row-odd'}
                      >
                        {statusIndex === 0 && (
                          <>
                            <td rowSpan={wo.statusDetails.length}>
                              {wo.woNumber}
                            </td>
                            <td rowSpan={wo.statusDetails.length}>
                              {wo.toModel ? `${wo.fromModel} → ${wo.toModel}` : wo.fromModel}
                            </td>
                            <td rowSpan={wo.statusDetails.length}>
                              {wo.picName || 'N/A'}
                            </td>
                            <td rowSpan={wo.statusDetails.length} className="text-center">
                              {wo.woQuantity}
                            </td>
                          </>
                        )}
                        <td>{getStatusLabel(status.status)}</td>
                        <td className="text-center">{status.quantity}</td>
                        <td className="text-right">${status.npfRate.toFixed(2)}</td>
                        <td className="text-right">${status.unitRate.toFixed(2)}</td>
                        <td className="text-right">${status.totalAmount.toFixed(2)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr className={woIndex % 2 === 0 ? 'row-even' : 'row-odd'}>
                      <td>{wo.woNumber}</td>
                      <td>{wo.toModel ? `${wo.fromModel} → ${wo.toModel}` : wo.fromModel}</td>
                      <td>{wo.picName || 'N/A'}</td>
                      <td className="text-center">{wo.woQuantity}</td>
                      <td colSpan="5" className="text-center">No status details</td>
                    </tr>
                  )}
                  {wo.statusDetails && wo.statusDetails.length > 0 && (
                    <tr className="subtotal-row">
                      <td colSpan="8" className="text-right">
                        <strong>Work Order Total:</strong>
                      </td>
                      <td className="text-right">
                        <strong>${wo.totalAmount.toFixed(2)}</strong>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              <tr className="grand-total-row">
                <td colSpan="8" className="text-right">
                  <strong>Grand Total:</strong>
                </td>
                <td className="text-right">
                  <strong>
                    ${qualityData.reduce((sum, wo) => sum + wo.totalAmount, 0).toFixed(2)}
                  </strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="action-bar no-print">
        <button onClick={onBack} className="btn-secondary" aria-label="Back to invoice">
          Back to Invoice
        </button>
        <button onClick={() => window.print()} className="btn-secondary" aria-label="Print report">
          Print
        </button>
      </div>
    </div>
  );
};

export default ProductQualitySupport;
